import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { IntegrationHoverOverlay } from '@/features/integrations/components/integration-hover-overlay';
import { IntegrationsIcon } from '@/features/integrations/components/integrations-icon';
import { TrackingReportPopulated } from '@/features/projects/kpi-tracking-entry/api/get-tracking-reports';
import { CustomTable } from '@/shared/components/table/table';
import { CustomTableHeader } from '@/shared/components/table/table-header';
import {
  CustomTableRow,
  TableRowAction,
  TableRowItemProps,
  TableRowProps,
} from '@/shared/components/table/table-row';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { TableBody } from '@/shared/components/ui/table';
import { getKpiUnitTranslation } from '@/shared/hooks/use-kpi-unit-translation';
import { getInitials } from '@/shared/utils/avatar-util';
import { formatDate, formatNumberWithDots } from '@/shared/utils/format';

type Props = {
  isContractor: boolean;
  reports: TrackingReportPopulated[];
  columns: string[];
  onOpenTrackingReport: (trackingReport: TrackingReportPopulated) => void;
  onDeleteTrackingReport: (trackingReportId: number) => void;
};

export const TrackingReportsTable = ({
  isContractor,
  reports,
  columns,
  onOpenTrackingReport,
  onDeleteTrackingReport,
}: Props) => {
  const translationShared = useTranslation('shared');
  const tShared = translationShared.t;
  const translationKpiTypes = useTranslation('kpiTypes', {
    nsMode: 'fallback',
  });
  const tKpiTypes = translationKpiTypes.t;
  const { t: tTable } = useTranslation('project', {
    keyPrefix: 'TrackingReports.TrackingReportsTable',
  });

  const columnWidths = [
    'w-[160px]', // Sub project name
    'w-[160px]', // Creator
    'w-[260px]', // Description
    'w-[200px]', // KPIs
    'flex-1', // Values
    'flex-1', // Documents
    'flex-1', // Date
  ];

  const tableRows: TableRowProps[] = reports
    ? reports.map((report, index): TableRowProps => {
        const row = createTableRow(report, tKpiTypes, tTable, columnWidths);
        return {
          type: 'single',
          showMenu: true,
          props: row,
          isFirstRow: index === 0,
          isLastRow: index === reports.length - 1,
          actions: [
            {
              action: isContractor ? (
                <p>{tShared('edit')}</p>
              ) : (
                <p>{tShared('open')}</p>
              ),
              onClick: () => onOpenTrackingReport(report),
            },
            isContractor && {
              action: <p>{tShared('delete')}</p>,
              onClick: () => onDeleteTrackingReport(report.id),
            },
          ].filter(Boolean) as TableRowAction[],
        };
      })
    : [];

  return (
    <CustomTable>
      <CustomTableHeader
        columns={columns}
        columnWidths={columnWidths}
        withContainer={false}
      />
      <TableBody>
        {tableRows.map((row, index) => {
          return (
            <CustomTableRow
              key={index}
              props={row.props}
              isFirstRow={row.isFirstRow}
              isLastRow={row.isLastRow}
              showMenu={row.showMenu}
              actions={row.actions}
              type="single"
            />
          );
        })}
      </TableBody>
    </CustomTable>
  );
};

const getEditedDate = (report: TrackingReportPopulated): Date | null => {
  const editedEntries = report.tracking_entries.filter(
    (entry) =>
      entry.updated_at &&
      new Date(entry.updated_at).getTime() !==
        new Date(entry.created_at).getTime(),
  );
  if (editedEntries.length === 0) return null;

  return editedEntries.reduce((latest, entry) => {
    const entryDate = new Date(entry.updated_at!);
    return entryDate > latest ? entryDate : latest;
  }, new Date(editedEntries[0].updated_at!));
};

const createTableRow = (
  report: TrackingReportPopulated,
  tKpiTypes: TFunction<'kpiTypes'>,
  tTable: TFunction<'project'>,
  widths: string[],
): TableRowItemProps[] => {
  const wasCreatedByIntegration = report.created_by_token;

  const reportSubProjectName: TableRowItemProps = {
    isLeftMost: true,
    width: widths[0],
    value: {
      type: 'custom',
      item: wasCreatedByIntegration ? (
        <IntegrationHoverOverlay>
          <div className="flex items-center gap-2">
            <IntegrationsIcon variant="white" />
            <span className="text-2xs font-normal">{report.project.name}</span>
          </div>
        </IntegrationHoverOverlay>
      ) : (
        <span className="text-2xs font-normal">{report.project.name}</span>
      ),
    },
  };
  const reportCreatorName = createTableRowCreator({
    name: report.creator_entity_name ?? '',
    width: widths[1],
  });
  const reportDescription = createTableRowString({
    text: report.description ?? '',
    width: widths[2],
  });

  const reportTrackingKpis: TableRowItemProps = {
    value: {
      type: 'custom',
      item: (
        <div className="flex flex-col gap-1">
          {report.tracking_entries.map((entry, idx) => (
            <span key={idx} className="truncate text-2xs">
              {entry.kpi.name} in{' '}
              {getKpiUnitTranslation(tKpiTypes, entry.kpi.unit)}
            </span>
          ))}
        </div>
      ),
    },
    width: widths[3],
  };
  const reportTrackingKpiValues: TableRowItemProps = {
    value: {
      type: 'custom',
      item: (
        <div className="flex flex-col gap-1">
          {report.tracking_entries.map((entry, idx) => (
            <span key={idx} className="text-2xs">
              {formatNumberWithDots(entry.value)}
            </span>
          ))}
        </div>
      ),
    },
    width: widths[4],
  };

  const reportDocumentsCount = createTableRowNumber({
    number: report.documents.length,
    width: widths[5],
  });

  const editedDate = getEditedDate(report);

  const reportDateCell: TableRowItemProps = {
    isRightMost: true,
    width: widths[6],
    value: {
      type: 'custom',
      item: editedDate ? (
        <span>
          {formatDate({ date: editedDate, formatType: 'digits' })}{' '}
          {tTable('edited' as any)}
        </span>
      ) : (
        <span>
          {formatDate({
            date: new Date(report.created_at),
            formatType: 'digits',
          })}
        </span>
      ),
    },
  };

  return [
    reportSubProjectName,
    reportCreatorName,
    reportDescription,
    reportTrackingKpis,
    reportTrackingKpiValues,
    reportDocumentsCount,
    reportDateCell,
  ];
};

const createTableRowCreator = ({
  name,
  isLeftMost,
  isRightMost,
  width,
}: {
  name: string;
  isLeftMost?: boolean;
  isRightMost?: boolean;
  width?: string;
}): TableRowItemProps => {
  return {
    isLeftMost: isLeftMost,
    isRightMost: isRightMost,
    value: {
      type: 'custom',
      item: (
        <div className="flex items-center gap-2">
          <Avatar size="sm">
            <AvatarFallback>{getInitials(name)}</AvatarFallback>
          </Avatar>
          <span className="text-2xs font-normal">{name}</span>
        </div>
      ),
    },
    width: width,
  };
};

const createTableRowString = ({
  text,
  isLeftMost,
  isRightMost,
  width,
}: {
  text: string;
  isLeftMost?: boolean;
  isRightMost?: boolean;
  width?: string;
}): TableRowItemProps => {
  return {
    isLeftMost: isLeftMost,
    isRightMost: isRightMost,
    value: { text: text, type: 'string' },
    width: width,
  };
};

const createTableRowNumber = ({
  number,
  isLeftMost,
  isRightMost,
  width,
}: {
  number: number;
  isLeftMost?: boolean;
  isRightMost?: boolean;
  width?: string;
}): TableRowItemProps => {
  return {
    isLeftMost: isLeftMost,
    isRightMost: isRightMost,
    value: { number: number, type: 'number' },
    width: width,
  };
};
